import { cn } from '@/lib/cn';

/**
 * Las viñetas de las tarjetas KPI del informe de proyecto: un punto por
 * evento, una barra por evento, un arco de avance o una tira partida en dos.
 * Son marcas, no gráficas: el número grande manda y esto le da forma.
 */

export function MiniDots({ dots }: { dots: Array<{ title: string; color: string; h: number }> }) {
  return (
    <div className="flex h-9 items-end gap-1" aria-hidden>
      {dots.map((d, i) => (
        <span key={i} title={d.title} className="w-2 rounded-full" style={{ height: `${Math.max(6, d.h)}px`, background: d.color }} />
      ))}
    </div>
  );
}

export function MiniBars({ bars, max }: { bars: Array<{ title: string; value: number; color: string }>; max?: number }) {
  const top = max ?? Math.max(1, ...bars.map((b) => b.value));
  return (
    <div className="flex h-11 items-end gap-0.5" aria-hidden>
      {bars.map((b, i) => (
        <span
          key={i}
          title={b.title}
          className="min-w-[4px] flex-1 rounded-t-sm"
          style={{ height: `${Math.max(3, (b.value / top) * 44)}px`, background: b.color }}
        />
      ))}
    </div>
  );
}

/**
 * Arco de avance: `parts` en orden, cada una con su color y su parte (0–100),
 * y el resto en la pista. La leyenda va al lado porque un arco sin cifras es
 * decoración.
 */
export function MiniArc({ parts, legend }: {
  parts: Array<{ pct: number; color: string }>;
  legend: Array<{ label: string; value: string; color: string }>;
}) {
  let acc = 0;
  const stops = parts
    .map((p) => {
      const from = acc;
      acc = Math.min(100, acc + Math.max(0, p.pct));
      return `${p.color} ${from}% ${acc}%`;
    })
    .concat([`var(--color-border-default) ${acc}% 100%`])
    .join(', ');
  return (
    <div className="flex items-center gap-3">
      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full" style={{ background: `conic-gradient(${stops})` }} aria-hidden>
        <span className="h-9 w-9 rounded-full bg-[var(--color-bg-surface)]" />
      </span>
      <ul className="space-y-0.5 text-[11px]">
        {legend.map((l) => (
          <li key={l.label} className="flex items-center gap-1.5 text-[var(--color-fg-muted)]">
            <span className="h-2 w-2 rounded-sm" style={{ background: l.color }} />
            {l.label}
            <span className="font-bold tabular-nums text-[var(--color-fg-primary)]">{l.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function MiniSplit({ parts }: { parts: Array<{ label: string; value: string; pct: number; color: string; title?: string }> }) {
  return (
    <div className="space-y-1.5">
      <div className="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-[var(--color-border-default)]" aria-hidden>
        {parts.map((p) => (
          <span key={p.label} title={p.title} className="h-full first:rounded-l-full last:rounded-r-full" style={{ width: `${Math.max(0, Math.min(100, p.pct))}%`, background: p.color }} />
        ))}
      </div>
      <ul className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]">
        {parts.map((p) => (
          <li key={p.label} className="flex items-center gap-1.5 text-[var(--color-fg-muted)]">
            <span className="h-2 w-2 rounded-sm" style={{ background: p.color }} />
            {p.label}
            <span className="font-bold tabular-nums text-[var(--color-fg-primary)]">{p.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** La tarjeta KPI que envuelve una viñeta. */
export function KpiCard({ label, value, chip, chipTone = 'accent', hint, valueTone, children, className }: {
  label: string;
  value: string;
  chip?: string;
  chipTone?: 'accent' | 'warn' | 'neutral';
  hint?: string;
  valueTone?: 'default' | 'accent' | 'warn' | 'purple';
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col gap-3 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-5', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-[10px] font-bold uppercase tracking-widest text-[var(--color-fg-muted)]">{label}</span>
        {chip && (
          <span
            className={cn(
              'rounded-full px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider',
              chipTone === 'warn'
                ? 'bg-[color-mix(in_srgb,var(--color-warn)_14%,transparent)] text-[var(--color-warn-ink)]'
                : chipTone === 'neutral'
                  ? 'bg-[var(--color-bg-elevated)] text-[var(--color-fg-muted)]'
                  : 'bg-[var(--color-accent-10)] text-[var(--color-accent-ink)]',
            )}
          >
            {chip}
          </span>
        )}
      </div>
      <div
        className={cn(
          'text-[2.5rem] font-bold leading-none tabular-nums tracking-tight',
          valueTone === 'accent'
            ? 'text-[var(--color-accent-ink)]'
            : valueTone === 'warn'
              ? 'text-[var(--color-warn-ink)]'
              : valueTone === 'purple'
                ? 'text-[var(--color-info)]'
                : 'text-[var(--color-fg-primary)]',
        )}
      >
        {value}
      </div>
      {children}
      {hint && <p className="text-xs leading-snug text-[var(--color-fg-subtle)]">{hint}</p>}
    </div>
  );
}
